import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Megaphone, KeyRound } from 'lucide-react';
import api from '@/lib/api';

const STATUS_COLUMNS = [
  { key: 'Requested', accent: 'border-l-slate-500', dot: 'bg-slate-400' },
  { key: 'In Progress', accent: 'border-l-primary', dot: 'bg-primary' },
  { key: 'In Review', accent: 'border-l-amber-500', dot: 'bg-amber-400' },
  { key: 'Published', accent: 'border-l-emerald-500', dot: 'bg-emerald-400' },
  { key: 'On Hold', accent: 'border-l-zinc-500/70', dot: 'bg-zinc-400' },
];

const CONTENT_TYPE_BADGE_COLORS = {
  'Blog Post': 'bg-primary/15 text-primary border-primary/30',
  'Newsletter': 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  'Social Post': 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30',
  'Landing Page': 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  'Page Optimization': 'bg-amber-500/15 text-amber-300 border-amber-500/30',
};


function isInCurrentMonth(isoDateStr) {
  if (!isoDateStr) return false;
  const d = new Date(isoDateStr);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  return d.getUTCMonth() === now.getUTCMonth() && d.getUTCFullYear() === now.getUTCFullYear();
}


// Pick the "current" keyword group (weekly_keywords record) whose date_range
// falls within the current calendar week. Falls back to the most recent group
// tied to the active strategy, then to the most recent group overall.
function pickCurrentWeekGroup(weeklyKeywords, activeStrategyId) {
  if (!weeklyKeywords || weeklyKeywords.length === 0) return null;
  const now = new Date();
  const dayOfWeek = now.getUTCDay(); // 0 = Sun
  const startOfWeek = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - dayOfWeek));
  const endOfWeek = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - dayOfWeek + 6, 23, 59, 59));
  const inWindow = weeklyKeywords.find(w => {
    if (!w.date_range) return false;
    const d = new Date(w.date_range);
    if (Number.isNaN(d.getTime())) return false;
    return d >= startOfWeek && d <= endOfWeek;
  });
  if (inWindow) return inWindow;
  if (activeStrategyId) {
    const forActive = weeklyKeywords
      .filter(w => w.monthly_strategy_id === activeStrategyId)
      .sort((a, b) => (b.date_range || '').localeCompare(a.date_range || ''));
    if (forActive.length > 0) return forActive[0];
  }
  return [...weeklyKeywords].sort((a, b) => (b.date_range || '').localeCompare(a.date_range || ''))[0] || null;
}


function formatWeekLabel(week) {
  if (!week) return '';
  const parts = [];
  if (week.week_number) parts.push(`Week ${week.week_number}`);
  if (week.date_range) {
    const d = new Date(week.date_range);
    if (!Number.isNaN(d.getTime())) {
      parts.push(d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }));
    }
  }
  return parts.join(' - ');
}


function formatDueDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}


function StatCard({ title, value, loading, testId }) {
  return (
    <Card className="border-white/10 bg-card shadow-none" data-testid={testId}>
      <CardContent className="p-4">
        <p className="text-[11px] uppercase tracking-[0.08em] font-medium text-muted-foreground">{title}</p>
        <p className="text-2xl sm:text-[28px] lg:text-[32px] font-medium font-mono tabular-nums leading-none mt-2 text-foreground">
          {loading ? '-' : value}
        </p>
      </CardContent>
    </Card>
  );
}


function KanbanCard({ item }) {
  const badgeColor = CONTENT_TYPE_BADGE_COLORS[item.content_type] || 'bg-secondary text-muted-foreground border-border';
  return (
    <div
      className="rounded-md border border-white/10 bg-card p-3 space-y-2"
      data-testid={`content-card-${item.id}`}
    >
      <p className="text-sm font-medium leading-tight text-foreground line-clamp-2">
        {item.content_title || 'Untitled'}
      </p>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Badge
          variant="outline"
          className={`text-[10px] uppercase tracking-[0.05em] font-medium ${badgeColor}`}
        >
          {item.content_type || 'Blog Post'}
        </Badge>
        {item.due_date && (
          <span className="text-[11px] text-muted-foreground font-mono tabular-nums" data-testid={`content-card-due-${item.id}`}>
            {formatDueDate(item.due_date)}
          </span>
        )}
      </div>
      {item.assigned_to && (
        <p className="text-[11px] text-muted-foreground" data-testid={`content-card-assignee-${item.id}`}>
          <span className="text-muted-foreground/70">Assigned:</span>{' '}
          <span className="text-foreground/90">{item.assigned_to}</span>
        </p>
      )}
    </div>
  );
}


function KanbanColumn({ status, items }) {
  const meta = STATUS_COLUMNS.find(c => c.key === status);
  return (
    <div
      className={`rounded-md border border-white/10 bg-secondary/20 border-l-4 ${meta.accent} flex flex-col`}
      data-testid={`kanban-column-${status.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="px-3 pt-3 pb-2 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-2">
          <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} aria-hidden />
          <p className="text-[11px] uppercase tracking-[0.08em] font-medium text-muted-foreground">{status}</p>
        </div>
        <span className="text-[11px] font-mono tabular-nums text-muted-foreground/80" data-testid={`kanban-count-${status.toLowerCase().replace(/\s+/g, '-')}`}>
          {items.length}
        </span>
      </div>
      <div className="p-2.5 space-y-2 min-h-[80px] max-h-[520px] overflow-y-auto">
        {items.length === 0 && (
          <p className="text-[11px] text-muted-foreground/60 italic px-1 py-2">Nothing here.</p>
        )}
        {items.map(it => <KanbanCard key={it.id} item={it} />)}
      </div>
    </div>
  );
}


function ActiveStrategyCard({ strategy }) {
  if (!strategy) {
    return (
      <Card className="border-white/10 bg-card shadow-none" data-testid="active-strategy-card-empty">
        <CardContent className="p-6 text-center">
          <p className="text-sm text-muted-foreground">No active strategy this month.</p>
        </CardContent>
      </Card>
    );
  }

  const rows = [
    { label: 'Theme / Focus', value: strategy.theme },
    { label: 'Target Keyword Clusters', value: strategy.keyword_clusters },
    { label: 'Notes for Sheena', value: strategy.notes_for_sheena },
  ];

  return (
    <Card
      className="border-primary/30 bg-primary/[0.04] shadow-none"
      data-testid="active-strategy-card"
    >
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-primary" aria-hidden />
          <p className="text-[11px] uppercase tracking-[0.08em] font-medium text-primary">Active Monthly Strategy</p>
          {strategy.month && (
            <span className="text-[11px] font-mono tabular-nums text-muted-foreground ml-auto">
              {new Date(strategy.month).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })}
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          {rows.map(r => (
            <div key={r.label} className="space-y-1" data-testid={`strategy-row-${r.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
              <p className="text-[11px] uppercase tracking-[0.08em] font-medium text-muted-foreground">{r.label}</p>
              <p className="text-sm text-foreground/90 whitespace-pre-wrap leading-relaxed">
                {r.value || <span className="text-muted-foreground/60 italic">Not set</span>}
              </p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}


const KEYWORD_TYPE_BADGE = {
  'Primary': 'bg-primary/15 text-primary border-primary/30',
  'Secondary': 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  'Long Tail': 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
};

const KEYWORD_INTENT_BADGE = {
  'Informational': 'bg-slate-500/15 text-slate-300 border-slate-500/30',
  'Commercial': 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30',
  'Transactional': 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  'Navigational': 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30',
};


function ThisWeekKeywordsCard({ week, keywords, loading }) {
  return (
    <Card className="border-white/10 bg-card shadow-none" data-testid="this-week-keywords-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-md bg-primary/10 border border-primary/20">
            <KeyRound className="h-3.5 w-3.5 text-primary" aria-hidden />
          </div>
          <p className="text-[11px] uppercase tracking-[0.08em] font-medium text-muted-foreground">This Week&apos;s Keywords</p>
          {week && (
            <span className="text-[11px] font-mono tabular-nums text-muted-foreground ml-auto" data-testid="this-week-keywords-label">
              {formatWeekLabel(week)}
            </span>
          )}
        </div>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : !week ? (
          <p className="text-sm text-muted-foreground italic" data-testid="this-week-keywords-empty">
            No keyword group scheduled for this week. Add one from the SEO Workspace to get started.
          </p>
        ) : keywords.length === 0 ? (
          <p className="text-sm text-muted-foreground italic" data-testid="this-week-keywords-empty">
            This week&apos;s group has no keywords yet.
          </p>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="this-week-keywords-list">
            {keywords.map(kw => (
              <li
                key={kw.id}
                className="rounded-md border border-white/10 bg-background/40 px-3 py-2 flex items-start justify-between gap-2"
                data-testid={`this-week-keyword-${kw.id}`}
              >
                <span className="text-sm text-foreground truncate">{kw.keyword}</span>
                <div className="flex items-center gap-1 shrink-0">
                  {kw.type && (
                    <Badge variant="outline" className={`text-[9px] uppercase tracking-[0.05em] ${KEYWORD_TYPE_BADGE[kw.type] || 'bg-secondary'}`}>
                      {kw.type}
                    </Badge>
                  )}
                  {kw.intent && (
                    <Badge variant="outline" className={`text-[9px] uppercase tracking-[0.05em] ${KEYWORD_INTENT_BADGE[kw.intent] || 'bg-secondary'}`}>
                      {kw.intent.slice(0, 4)}
                    </Badge>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}


export default function DigitalMarketingOverviewPage() {
  const [loading, setLoading] = useState(true);
  const [weeklyKeywords, setWeeklyKeywords] = useState([]);
  const [contentCalendar, setContentCalendar] = useState([]);
  const [activeStrategy, setActiveStrategy] = useState(null);
  const [keywords, setKeywords] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [wk, cc, strat, kw] = await Promise.all([
          api.get('/weekly-keywords'),
          api.get('/content-calendar'),
          api.get('/monthly-strategies?status=Active'),
          api.get('/keywords'),
        ]);
        if (cancelled) return;
        setWeeklyKeywords(wk.data || []);
        setContentCalendar(cc.data || []);
        setKeywords(kw.data || []);
        // Prefer the strategy whose month matches the current calendar month;
        // fall back to the most recent Active record.
        const now = new Date();
        const activeList = strat.data || [];
        const currentMonthMatch = activeList.find(s => {
          if (!s.month) return false;
          const d = new Date(s.month);
          return d.getUTCMonth() === now.getUTCMonth() && d.getUTCFullYear() === now.getUTCFullYear();
        });
        setActiveStrategy(currentMonthMatch || activeList[0] || null);
      } catch (e) {
        console.error('Failed to load Digital Marketing overview', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ─── Stat calculations ─────────────────────────────────────────────────
  const keywordsThisMonth = useMemo(
    () => weeklyKeywords.filter(w => isInCurrentMonth(w.date_range)).length,
    [weeklyKeywords]
  );
  const pendingRequests = useMemo(
    () => contentCalendar.filter(c => c.status === 'Requested').length,
    [contentCalendar]
  );
  const publishedThisMonth = useMemo(
    () => contentCalendar.filter(c => c.status === 'Published' && isInCurrentMonth(c.due_date)).length,
    [contentCalendar]
  );

  // ─── Kanban grouping ───────────────────────────────────────────────────
  const grouped = useMemo(() => {
    const g = Object.fromEntries(STATUS_COLUMNS.map(c => [c.key, []]));
    for (const item of contentCalendar) {
      if (g[item.status]) g[item.status].push(item);
    }
    // Sort within column: due_date asc, then title
    for (const key of Object.keys(g)) {
      g[key].sort((a, b) => {
        const da = a.due_date || '9999-99-99';
        const db = b.due_date || '9999-99-99';
        if (da !== db) return da.localeCompare(db);
        return (a.content_title || '').localeCompare(b.content_title || '');
      });
    }
    return g;
  }, [contentCalendar]);

  // ─── This Week's Keywords ─────────────────────────────────────────────
  const currentWeek = useMemo(
    () => pickCurrentWeekGroup(weeklyKeywords, activeStrategy?.id),
    [weeklyKeywords, activeStrategy]
  );
  const currentWeekKeywords = useMemo(
    () => currentWeek ? keywords.filter(k => k.week_id === currentWeek.id) : [],
    [keywords, currentWeek]
  );

  return (
    <div className="p-6 space-y-6" data-testid="digital-marketing-overview">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-md bg-primary/10 border border-primary/20">
          <Megaphone className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-[22px] font-medium tracking-tight" data-testid="dm-overview-title">
            Digital Marketing Overview
          </h1>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          title="Keywords This Month"
          value={keywordsThisMonth}
          loading={loading}
          testId="stat-keywords-this-month"
        />
        <StatCard
          title="Pending Content Requests"
          value={pendingRequests}
          loading={loading}
          testId="stat-pending-content-requests"
        />
        <StatCard
          title="Published This Month"
          value={publishedThisMonth}
          loading={loading}
          testId="stat-published-this-month"
        />
      </div>

      {/* This week's keywords */}
      <ThisWeekKeywordsCard week={currentWeek} keywords={currentWeekKeywords} loading={loading} />

      {/* Kanban board */}
      <div className="space-y-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-base font-medium text-foreground">Content Calendar</h2>
          <p className="text-[11px] text-muted-foreground uppercase tracking-[0.08em]">Read-only overview</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="content-kanban">
          {STATUS_COLUMNS.map(col => (
            <KanbanColumn key={col.key} status={col.key} items={grouped[col.key] || []} />
          ))}
        </div>
      </div>

      {/* Active strategy */}
      <ActiveStrategyCard strategy={activeStrategy} />
    </div>
  );
}
